import axios, { AxiosError } from 'axios';
import * as React from 'react';
import { requestPasswordUpdate } from '../asyncHelpers/changePasswordService';
import { RequestStatus } from '../sic-lib/enums/RequestStatus';
import { UpdatePasswordInputs, UpdatePasswordRequest } from '../sic-lib/types/Password';
import { useAppDispatch } from '../store/hooks';
import { createToast } from '../store/notify';
import { createErrorToast } from '../utils/notifyHelpers';

/**
 * useAsyncModifyPassword
 *
 * @description
 * React hook providing request state management and cancellation for the
 * requestPasswordUpdate HTTP request
 */
export default function useAsyncModifyPassword() {
    const dispatch = useAppDispatch();
    const request_token = axios.CancelToken.source();

    React.useEffect(() => {
        return () => {
            request_token.cancel();
        };
    }, []);

    const [requestStatus, setRequestStatus] = React.useState<RequestStatus>(RequestStatus.none);

    const asyncModifyPassword = async (
        passwordUpdate: UpdatePasswordInputs,
    ): Promise<UpdatePasswordRequest | undefined> => {
        try {
            setRequestStatus(RequestStatus.active);

            await requestPasswordUpdate(passwordUpdate, request_token.token);

            setRequestStatus(RequestStatus.success);

            return { ...passwordUpdate };
        } catch (e: any) {
            if (!axios.isCancel(e)) {
                if (axios.isAxiosError(e)) {
                    const error = e as AxiosError;
                    if (e.response?.status == 404 || e.response?.status == 400) {
                        /* Notify user of failed http request */
                        const toastData = createErrorToast(e.response.data);

                        dispatch(createToast({ toastData }));
                    }
                }

                /* Update state */
                setRequestStatus(RequestStatus.error);
            }
        }
    };

    return {
        requestStatus,
        asyncModifyPassword,
    };
}
